import React from "react";

import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import WarningIcon from "@material-ui/icons/Warning";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";

import { StatusBadge } from "../../components/ui";
import { i18n } from "../../translate/i18n";
import { getTokens } from "../../styles/tokens";
import { useDate } from "../../hooks/useDate";
import { formatAmount, stageLabel } from "./helpers";

// Mesmo tom do selo de estágio da tabela da régua.
const STAGE_TONE = { before: "info", due: "warning", after: "error" };

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    dialogSubtitle: {
      fontSize: 13,
      fontWeight: 400,
      color: t.textSecondary,
      marginTop: 2,
      letterSpacing: 0,
    },
    loadingRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      fontSize: 13,
      color: t.textSecondary,
    },
    summary: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 12,
      padding: "12px 14px",
      marginBottom: 16,
      borderRadius: t.radius.sm,
      border: `1px solid ${t.line}`,
      backgroundColor: t.muted,
    },
    summaryValue: {
      fontSize: 24,
      fontWeight: 600,
      lineHeight: 1,
      color: t.text,
      fontVariantNumeric: "tabular-nums",
    },
    summaryLabel: {
      flex: "1 1 auto",
      fontSize: 13,
      color: t.textSecondary,
    },
    warningBox: {
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      padding: "10px 14px",
      marginBottom: 16,
      borderRadius: t.radius.sm,
      backgroundColor: t.status.warn.bg,
      color: t.status.warn.fg,
      fontSize: 13,
      lineHeight: 1.5,
    },
    warningIcon: {
      marginTop: 1,
      flex: "none",
    },
    warningTitle: {
      fontWeight: 600,
    },
    warningList: {
      margin: 0,
      paddingLeft: 18,
    },
    sectionTitle: {
      fontSize: 13,
      fontWeight: 600,
      color: t.text,
      marginBottom: 8,
    },
    sample: {
      padding: "12px 14px",
      marginTop: 8,
      borderRadius: t.radius.sm,
      border: `1px solid ${t.line}`,
      backgroundColor: t.paper,
    },
    sampleHeader: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "baseline",
      gap: "2px 10px",
      marginBottom: 6,
    },
    sampleName: {
      fontSize: 14,
      fontWeight: 600,
      color: t.text,
    },
    sampleMeta: {
      color: t.textSecondary,
      fontSize: 12,
    },
    sampleMessage: {
      fontSize: 13.5,
      lineHeight: 1.5,
      color: t.text,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    },
    sampleButtons: {
      marginTop: 6,
      fontWeight: 600,
    },
    helperText: {
      fontSize: 13,
      color: t.textSecondary,
    },
    btnWrapper: {
      position: "relative",
    },
    buttonProgress: {
      color: theme.palette.primary.main,
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -12,
      marginLeft: -12,
    },
  };
});

const BillingRulePreviewModal = ({ open, onClose, rule, preview, loading, running, onRunNow }) => {
  const classes = useStyles();
  const { dateToClient } = useDate();

  const samples = Array.isArray(preview?.samples) ? preview.samples : [];
  const warnings = Array.isArray(preview?.warnings) ? preview.warnings : [];
  const count = Number(preview?.count) || 0;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!running) onClose();
      }}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      aria-labelledby="billing-rule-preview-title"
    >
      <DialogTitle id="billing-rule-preview-title">
        {i18n.t("billingRules.preview.title")}
        {rule && <div className={classes.dialogSubtitle}>{rule.name}</div>}
      </DialogTitle>
      <DialogContent>
        {loading && (
          <div className={classes.loadingRow}>
            <CircularProgress size={20} />
            <span>{i18n.t("billingRules.preview.loading")}</span>
          </div>
        )}

        {!loading && preview && (
          <>
            <div className={classes.summary}>
              <span className={classes.summaryValue}>{count}</span>
              <span className={classes.summaryLabel}>{i18n.t("billingRules.preview.count")}</span>
              {rule && (
                <StatusBadge
                  color={STAGE_TONE[rule.stage] || STAGE_TONE.due}
                  label={stageLabel(rule.stage, rule.offsetDays)}
                />
              )}
            </div>

            {warnings.length > 0 && (
              <div className={classes.warningBox} role="alert">
                <WarningIcon className={classes.warningIcon} fontSize="small" />
                <div>
                  <div className={classes.warningTitle}>{i18n.t("billingRules.preview.warnings")}</div>
                  <ul className={classes.warningList}>
                    {warnings.map((w, idx) => (
                      <li key={idx}>{w}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <div className={classes.sectionTitle}>{i18n.t("billingRules.preview.samples")}</div>
            {samples.length === 0 && (
              <div className={classes.helperText}>{i18n.t("billingRules.preview.noSamples")}</div>
            )}
            {samples.map((sample, idx) => (
              <div key={idx} className={classes.sample}>
                <div className={classes.sampleHeader}>
                  <span className={classes.sampleName}>{sample.contactName || "-"}</span>
                  {sample.contactNumber && (
                    <span className={classes.sampleMeta}>{sample.contactNumber}</span>
                  )}
                  {sample.dueDate && (
                    <span className={classes.sampleMeta}>
                      {i18n.t("billingRules.preview.dueDate")}: {dateToClient(sample.dueDate)}
                    </span>
                  )}
                  {sample.amount !== undefined && sample.amount !== null && (
                    <span className={classes.sampleMeta}>
                      {i18n.t("billingRules.preview.amount")}: {formatAmount(sample.amount)}
                    </span>
                  )}
                </div>
                <div className={classes.sampleMessage}>
                  {sample.renderedMessage || "-"}
                  {Array.isArray(sample.buttons) && sample.buttons.length > 0 && (
                    <div className={classes.sampleButtons}>🔘 {sample.buttons.join(" · ")}</div>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={running}>
          {i18n.t("billingRules.buttons.close")}
        </Button>
        <Button
          onClick={onRunNow}
          color="primary"
          variant="contained"
          startIcon={<PlayArrowIcon />}
          disabled={loading || running || !preview || count <= 0}
          className={classes.btnWrapper}
        >
          {i18n.t("billingRules.buttons.runNow")}
          {running && <CircularProgress size={24} className={classes.buttonProgress} />}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BillingRulePreviewModal;
